import React from "react";

export default function PrivacyPolicy() {
  return (
    <main style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <p style={styles.badge}>NGC Financial Services / Direct Money</p>
          <h1 style={styles.title}>Privacy Policy</h1>
          <h2 style={styles.subtitle}>Politique de Confidentialité</h2>
          <p style={styles.updated}>Last updated / Dernière mise à jour : May 2026</p>
        </div>
      </section>

      <section style={styles.container}>
        <div style={styles.card}>
          <LanguageBlock
            lang="English"
            title="Privacy Policy"
            sections={englishSections}
          />

          <div style={styles.divider} />

          <LanguageBlock
            lang="Français"
            title="Politique de Confidentialité"
            sections={frenchSections}
          />
        </div>
      </section>
    </main>
  );
}

function LanguageBlock({ lang, title, sections }) {
  return (
    <div>
      <div style={styles.languageHeader}>
        <span style={styles.langPill}>{lang}</span>
        <h2 style={styles.docTitle}>{title}</h2>
      </div>

      {sections.map((section) => (
        <section key={section.title} style={styles.section}>
          <h3 style={styles.heading}>{section.title}</h3>

          {section.paragraphs?.map((text, index) => (
            <p key={index} style={styles.paragraph}>
              {text}
            </p>
          ))}

          {section.items && (
            <ul style={styles.list}>
              {section.items.map((item, index) => (
                <li key={index} style={styles.listItem}>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

const englishSections = [
  {
    title: "1. Introduction",
    paragraphs: [
      "This Privacy Policy explains how NGC Financial Services (“we”, “our”, “us”) collects, uses, stores, shares and protects the personal data of users (“you”, “your”) when using the Direct Money mobile application and our related services.",
      "We are committed to protecting the security, confidentiality and integrity of your personal information in accordance with applicable international data protection standards.",
    ],
  },
  {
    title: "2. Data We Collect",
    paragraphs: [
      "When you use our services, we may collect the following categories of data:",
    ],
    items: [
      "Identification data: first name, last name, email address, phone number, login credentials and user profile information.",
      "Financial data: transaction details, amounts, currencies, recipients, transaction history and wallet-related data.",
      "KYC data: identity documents, photographs and identity verification information required for Know Your Customer and anti-money laundering obligations.",
      "Technical data: IP address, device type, operating system, usage logs and session identifiers.",
      "Communication data: customer support exchanges, notifications, emails sent or received.",
    ],
  },
  {
    title: "3. Purposes of Processing",
    items: [
      "Providing and managing financial services.",
      "Processing transactions and money transfers.",
      "Identity verification and regulatory compliance, including KYC and AML.",
      "Securing accounts and preventing fraud.",
      "Improving user experience and service quality.",
      "Communicating with users, including notifications and support.",
      "Complying with legal and regulatory obligations.",
    ],
  },
  {
    title: "4. Legal Basis",
    paragraphs: [
      "We process your personal data based on the performance of our contract with you, compliance with legal obligations, your consent where required, and our legitimate interests such as security, fraud prevention and service improvement.",
    ],
  },
  {
    title: "5. Data Sharing",
    paragraphs: [
      "We may share your data only when necessary to provide our services or comply with the law.",
    ],
    items: [
      "With financial partners or technical service providers required to deliver our services.",
      "With regulatory, judicial or law enforcement authorities where required by law.",
      "With service providers such as hosting, email, security and infrastructure providers.",
      "We never sell your personal data to third parties.",
    ],
  },
  {
    title: "6. Data Security",
    paragraphs: [
      "We implement appropriate technical and organizational security measures, including:",
    ],
    items: [
      "Data encryption and secure communications.",
      "Secure authentication, access control and JWT-based authorization.",
      "Monitoring of suspicious transactions.",
      "Protection against unauthorized access.",
      "However, no system is completely secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    title: "7. Data Retention",
    paragraphs: [
      "We retain your data only for as long as necessary to provide our services, comply with legal, financial and tax obligations, resolve disputes and enforce our agreements.",
    ],
  },
  {
    title: "8. Your Rights",
    paragraphs: [
      "Depending on applicable law, you may have the following rights:",
    ],
    items: [
      "Right to access your data.",
      "Right to correct inaccurate data.",
      "Right to request deletion.",
      "Right to object to certain processing.",
      "Right to data portability.",
      "Right to withdraw consent where processing is based on consent.",
      "You may exercise these rights by contacting us using the details below.",
    ],
  },
  {
    title: "9. International Transfers",
    paragraphs: [
      "Your data may be processed or stored in countries other than your country of residence. Where this occurs, we apply appropriate safeguards to protect your information.",
    ],
  },
  {
    title: "10. Changes to This Policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time. Any significant changes may be communicated through the application, website or by email.",
    ],
  },
  {
    title: "11. Contact",
    paragraphs: [
      "For questions regarding this Privacy Policy, contact us at support@ngcfinancialservices.com or visit https://ngcfinancialservices.com.",
    ],
  },
];

const frenchSections = [
  {
    title: "1. Introduction",
    paragraphs: [
      "La présente Politique de Confidentialité décrit la manière dont NGC Financial Services (« nous », « notre », « nos ») collecte, utilise, conserve, partage et protège les données personnelles des utilisateurs (« vous », « votre ») dans le cadre de l’utilisation de notre application mobile Direct Money ainsi que de nos services associés.",
      "Nous nous engageons à assurer la sécurité, la confidentialité et l’intégrité de vos informations personnelles conformément aux normes internationales applicables en matière de protection des données.",
    ],
  },
  {
    title: "2. Données collectées",
    paragraphs: [
      "Dans le cadre de l’utilisation de nos services, nous pouvons collecter les catégories de données suivantes :",
    ],
    items: [
      "Données d’identification : nom, prénom, adresse e-mail, numéro de téléphone, identifiants de connexion et informations de profil utilisateur.",
      "Données financières : informations relatives aux transactions, montants, devises, destinataires, historique des opérations et données liées au portefeuille électronique.",
      "Données KYC : documents d’identification, photographies et informations d’identité conformément aux obligations réglementaires en matière de connaissance client et de lutte contre le blanchiment d’argent.",
      "Données techniques : adresse IP, type d’appareil, système d’exploitation, logs d’utilisation et identifiants de session.",
      "Données de communication : échanges avec le support client, notifications, e-mails envoyés ou reçus.",
    ],
  },
  {
    title: "3. Finalités du traitement",
    items: [
      "Fourniture et gestion des services financiers.",
      "Exécution des transactions et transferts d’argent.",
      "Vérification d’identité et conformité réglementaire, notamment KYC et AML.",
      "Sécurisation des comptes et prévention de la fraude.",
      "Amélioration de l’expérience utilisateur et de la qualité des services.",
      "Communication avec l’utilisateur, notamment notifications et assistance.",
      "Respect des obligations légales et réglementaires.",
    ],
  },
  {
    title: "4. Base légale",
    paragraphs: [
      "Le traitement de vos données repose sur l’exécution du contrat relatif à la fourniture de nos services, le respect d’obligations légales, votre consentement lorsque celui-ci est requis, ainsi que notre intérêt légitime, notamment pour la sécurité, la prévention de la fraude et l’amélioration des services.",
    ],
  },
  {
    title: "5. Partage des données",
    paragraphs: [
      "Nous pouvons partager vos données uniquement lorsque cela est nécessaire à la fourniture de nos services ou lorsque la loi l’exige.",
    ],
    items: [
      "Avec des partenaires financiers ou prestataires techniques nécessaires à l’exécution des services.",
      "Avec des autorités réglementaires, judiciaires ou compétentes lorsque la loi l’exige.",
      "Avec des fournisseurs de services tels que l’hébergement, l’e-mail, la sécurité et l’infrastructure technique.",
      "Nous ne vendons jamais vos données personnelles à des tiers.",
    ],
  },
  {
    title: "6. Sécurité des données",
    paragraphs: [
      "Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles adaptées, notamment :",
    ],
    items: [
      "Chiffrement des données et communications sécurisées.",
      "Authentification sécurisée, contrôle d’accès et autorisation basée sur JWT.",
      "Surveillance des transactions suspectes.",
      "Protection contre les accès non autorisés.",
      "Cependant, aucun système n’étant totalement sécurisé, nous ne pouvons garantir une sécurité absolue.",
    ],
  },
  {
    title: "7. Conservation des données",
    paragraphs: [
      "Vos données sont conservées pendant la durée nécessaire à l’exécution des services, au respect des obligations légales, financières et fiscales, à la résolution de litiges et à l’application de nos accords.",
    ],
  },
  {
    title: "8. Vos droits",
    paragraphs: [
      "Conformément aux réglementations applicables, vous pouvez disposer des droits suivants :",
    ],
    items: [
      "Droit d’accès à vos données.",
      "Droit de rectification.",
      "Droit de suppression.",
      "Droit d’opposition à certains traitements.",
      "Droit à la portabilité.",
      "Droit de retirer votre consentement lorsque le traitement repose sur celui-ci.",
      "Vous pouvez exercer ces droits en nous contactant via les coordonnées indiquées ci-dessous.",
    ],
  },
  {
    title: "9. Transferts internationaux",
    paragraphs: [
      "Vos données peuvent être traitées ou stockées dans des pays autres que votre pays de résidence. Dans ce cas, nous veillons à appliquer des garanties appropriées afin de protéger vos informations.",
    ],
  },
  {
    title: "10. Modifications",
    paragraphs: [
      "Nous nous réservons le droit de modifier la présente Politique de Confidentialité à tout moment. Toute modification importante pourra être communiquée via l’application, le site web ou par e-mail.",
    ],
  },
  {
    title: "11. Contact",
    paragraphs: [
      "Pour toute question relative à la présente Politique de Confidentialité, vous pouvez nous contacter à support@ngcfinancialservices.com ou visiter https://ngcfinancialservices.com.",
    ],
  },
];

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f8fafc",
    color: "#0f172a",
  },
  hero: {
    background: "linear-gradient(135deg, #152556 0%, #223875 100%)",
    color: "#fff",
    padding: "72px 16px 96px",
  },
  heroInner: {
    maxWidth: 980,
    margin: "0 auto",
  },
  badge: {
    display: "inline-block",
    background: "rgba(137, 114, 61, 0.22)",
    color: "#f8e7b0",
    border: "1px solid rgba(248, 231, 176, 0.28)",
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 18,
  },
  title: {
    fontSize: "clamp(34px, 6vw, 56px)",
    lineHeight: 1.05,
    margin: 0,
    fontWeight: 900,
    letterSpacing: "-0.04em",
  },
  subtitle: {
    fontSize: "clamp(22px, 4vw, 34px)",
    margin: "10px 0 0",
    color: "rgba(255,255,255,0.82)",
    fontWeight: 700,
  },
  updated: {
    marginTop: 18,
    color: "rgba(255,255,255,0.72)",
    fontWeight: 600,
  },
  container: {
    maxWidth: 980,
    margin: "-56px auto 0",
    padding: "0 16px 64px",
  },
  card: {
    background: "#fff",
    borderRadius: 28,
    padding: "clamp(24px, 4vw, 44px)",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.12)",
    border: "1px solid rgba(226, 232, 240, 0.9)",
  },
  languageHeader: {
    marginBottom: 28,
  },
  langPill: {
    display: "inline-block",
    background: "#f5f1e8",
    color: "#89723d",
    borderRadius: 999,
    padding: "7px 12px",
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 12,
  },
  docTitle: {
    color: "#152556",
    fontSize: "clamp(28px, 5vw, 40px)",
    margin: 0,
    fontWeight: 900,
    letterSpacing: "-0.03em",
  },
  section: {
    marginTop: 26,
  },
  heading: {
    color: "#152556",
    fontSize: "clamp(19px, 3vw, 24px)",
    marginBottom: 10,
    fontWeight: 850,
  },
  paragraph: {
    color: "#334155",
    fontSize: 16,
    lineHeight: 1.75,
    margin: "0 0 12px",
  },
  list: {
    margin: "10px 0 0",
    paddingLeft: 22,
  },
  listItem: {
    color: "#334155",
    fontSize: 16,
    lineHeight: 1.75,
    marginBottom: 8,
  },
  divider: {
    height: 1,
    background: "#e2e8f0",
    margin: "46px 0",
  },
};